import React from "react";
import {
	Dialog,
	DialogActions,
	Button,
	Typography,
	IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

const VITE_DOCUMENT_PATH =
	import.meta.env.VITE_DOCUMENT_PATH ||
	"https://mill-cmms.roanokesteel.com/vaultix/";

const useStyles = makeStyles((theme) => ({
	viewerModal: {
		"& .MuiDialog-paper": {
			margin: 0,
			maxHeight: "100vh",
			height: "100vh",
			maxWidth: "100vw",
			width: "100vw",
			borderRadius: 0,
			display: "flex",
			flexDirection: "column",
		},
	},
	viewerModalWindowed: {
		"& .MuiDialog-paper": {
			margin: "32px",
			maxHeight: "90vh",
			height: "90vh",
			maxWidth: "90vw",
			width: "90vw",
			borderRadius: "8px",
			display: "flex",
			flexDirection: "column",
		},
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "12px 16px",
		borderBottom: "1px solid #e0e0e0",
		backgroundColor: "#f5f5f5",
		flexShrink: 0,
	},
	title: {
		fontSize: "16px",
		fontWeight: 600,
		color: "#333",
		flex: 1,
		marginRight: "16px",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	controls: {
		display: "flex",
		gap: "8px",
		alignItems: "center",
	},
	viewerContent: {
		flex: "1 1 auto",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#fafafa",
		padding: "16px",
		overflow: "hidden",
		minHeight: 0,
	},
	imageContainer: {
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
		minHeight: "400px",
	},
	image: {
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		objectPosition: "center",
		borderRadius: "4px",
		boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
	},
	fallbackImage: {
		maxWidth: "300px",
		maxHeight: "300px",
		opacity: 0.5,
		objectFit: "contain",
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
	},
	actions: {
		padding: "16px",
		borderTop: "1px solid #e0e0e0",
		backgroundColor: "#f5f5f5",
		flexShrink: 0,
		justifyContent: "flex-start",
		alignItems: "center",
		minHeight: "64px",
		height: "64px",
	},
	closeButton: {
		minWidth: "120px",
	},
}));

interface DocumentViewerModalProps {
	open: boolean;
	onClose: () => void;
	title: string;
	documentPath?: string;
	fallbackPath?: string;
	extension?: string;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
	open,
	onClose,
	title,
	documentPath,
	fallbackPath,
	extension,
}) => {
	const classes = useStyles();
	const [fullscreen, setFullscreen] = React.useState(false);
	const [imageError, setImageError] = React.useState(false);

	const toggleFullscreen = () => {
		setFullscreen(!fullscreen);
	};

	const handleImageError = () => {
		setImageError(true);
	};

	const isRenderableImage = (ext?: string) => {
		if (!ext) return false;
		const renderableExtensions = [
			"PNG",
			"JPG",
			"JPEG",
			"PDF",
			"GIF",
			"BMP",
			"SVG",
		];
		return renderableExtensions.includes(ext.toUpperCase());
	};

	const getDocumentUrl = () => {
		if (documentPath) {
			return `${VITE_DOCUMENT_PATH}${documentPath}`;
		}
		return fallbackPath || "";
	};

	React.useEffect(() => {
		if (open) {
			setImageError(false);
		}
	}, [open]);

	if (!isRenderableImage(extension)) {
		return null;
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullWidth
			className={fullscreen ? classes.viewerModal : classes.viewerModalWindowed}
		>
			<div className={classes.header}>
				<Typography className={classes.title} title={title}>
					{title}
				</Typography>
				<div className={classes.controls}>
					<IconButton
						size="small"
						onClick={toggleFullscreen}
						title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}
					>
						{fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
					</IconButton>
					<IconButton size="small" onClick={onClose} title="Close">
						<CloseIcon />
					</IconButton>
				</div>
			</div>

			<div className={classes.viewerContent}>
				<div className={classes.imageContainer}>
					{!imageError ? (
						<>
							{extension?.toUpperCase() === "PDF" ? (
								<object
									data={getDocumentUrl()}
									type="application/pdf"
									style={{
										width: "100%",
										height: "100%",
										minHeight: "500px",
									}}
								>
									<img
										src="img/notFound.jpg"
										alt="Document not available"
										className={classes.fallbackImage}
									/>
								</object>
							) : (
								<img
									src={getDocumentUrl()}
									alt={title}
									className={classes.image}
									onError={handleImageError}
									loading="lazy"
								/>
							)}
						</>
					) : (
						<img
							src="img/notFound.jpg"
							alt="Document not available"
							className={classes.fallbackImage}
						/>
					)}
				</div>
			</div>

			<DialogActions className={classes.actions}>
				<Button
					variant="contained"
					color="inherit"
					className={classes.closeButton}
					onClick={onClose}
				>
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	);
};
